<script setup lang="ts">
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import L, {
  type DivIcon,
  type LeafletMouseEvent,
  type Map as LeafletMap,
  type Marker as LeafletMarker,
  type PathOptions,
} from 'leaflet'
import {
  fromLeaflet,
  lineToLeaflet,
  mountVueInLeaflet,
  polygonToLeaflet,
  toLeaflet,
} from '@/shared/lib'
import { VInventMapMarker } from '@/shared/ui'
import { VInventMapCluster } from '@/shared/ui/VInventMapCluster'
import { VInventMapInfoMarker } from '@/shared/ui/VInventMapInfoMarker'
import { VTransformMarker } from '@/shared/ui'
import { createLeafletAdapter } from '@/widgets/InventoryMap/model/adapters/leafletAdapter.ts'
import type {
  MapAdapterEntity,
  MapComponentExpose,
  MapLocation,
} from '@/widgets/InventoryMap/model/adapters/types.ts'
import type { InventObject } from '@/widgets/InventoryMap/model/classes/InventObject.ts'
import {
  INVENTORY_LINE_REMOVE_HIT_OPACITY,
  resolveInventoryLineRemoveHitWidth,
} from '@/widgets/InventoryMap/model/lineHitArea.ts'

type MapType = 'scheme' | 'satellite' | 'hybrid'

type Props = {
  center: number[]
  zoom: number
  selectedObjects: number[]
  mapType: MapType
  pointerCount: number
  editFeature: any
  objects: any
  action: string
  clusterMethod: any
  zoomSwitchValue: number
  zoomSmallMarker: number
  editSelectObject: any
  editNewPoint: any
  isEditNewPoint: boolean
  isPlaceMode: boolean
  placePointerType: any
  addObjectType: any
  pointerScreenCoordinates: number[]
  getFeatureStyle: (object: InventObject) => Record<string, any>
  canRelocateObject: (object: InventObject) => boolean
  objectMoveBlockedHandler: (object: InventObject) => void
  objectClickHandler: (object: InventObject, event: MouseEvent) => void
  objectEndDragHandler: (object: InventObject, coordinates: any) => void
  objectStartDragHandler: (object: InventObject) => void
  objectMoveDragHandler: (object: InventObject, coordinates: any) => void
  clusterSelectHandler: (ids: number[]) => void
  dragStartEditHandler: () => void
  dragMoveEditHandler: (index: number, coordinates: any) => void
  dragEndEditHandler: () => void
  clickEditHandler: (index: number, event: any) => void
  clickAddPointHandler: () => void
  updateCoordsHandler: () => void
  mapCenteredHandler: (coords: number[]) => void
  selectCoordsType: '' | 'square' | 'circle'
  isAreaSelectMapDragEnabled: boolean
  selectedHandler: (coords: any) => void
  updateHandler: (event: any) => void
  mouseMoveHandler: (target: any, event: any) => void
  clickHandler: (target: any, event: any) => void
  mouseUpHandler: (target: any, event: any) => void | Promise<void>
  mouseDownHandler: (target: any, event: any) => void
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (event: 'update:center', value: number[]): void
  (event: 'update:zoom', value: number): void
  (event: 'update:selectedObjects', value: number[]): void
}>()

const mapEl = ref<HTMLElement | null>(null)
const leafletMap = ref<LeafletMap | null>(null)
const adapterState = ref<ReturnType<typeof createLeafletAdapter> | null>(null)
const markersLayer = ref<L.LayerGroup | null>(null)
const featuresLayer = ref<L.LayerGroup | null>(null)
const groupedMarkersLayer = ref<L.LayerGroup | null>(null)
const clusterLayer = ref<L.MarkerClusterGroup | null>(null)
const previewLayer = ref<L.LayerGroup | null>(null)
const isApplyingExternalLocation = ref(false)
const pendingLocation = ref<MapLocation | null>(null)
const pendingBehaviors = ref<string[] | null>(null)
const markerObjectMap = new WeakMap<LeafletMarker, InventObject>()
const markerDestroyMap = new Map<LeafletMarker, () => void>()
const previewDestroyFns = new Set<() => void>()
const isMarkerClusterReady = ref(false)
const isEditMarkerDragging = ref(false)

type OffsetMarkerEntry = { item: InventObject; pixelOffset: [number, number] }
type GroupMarkerEntry = { coordinates: [number, number]; items: InventObject[] }
type ObjectMarkerOptions = {
  offset?: [number, number]
  coordinates?: [number, number]
  allowDrag?: boolean
}
type DrawPolygonCoordinates = [number, number][][]
type DragLineCoordinates = [number, number][]
type DragPolygonCoordinates = DragLineCoordinates[]
type ActiveFeatureDrag = {
  object: InventObject
  start: [number, number]
  originalCoordinates: DragLineCoordinates | DragPolygonCoordinates
}
const isOffsetMarkerEntry = (entry: InventObject | OffsetMarkerEntry): entry is OffsetMarkerEntry =>
  'item' in entry
const activeFeatureDrag = ref<ActiveFeatureDrag | null>(null)

const centerModel = computed({
  get: () => props.center,
  set: (value) => emit('update:center', value),
})

const zoomModel = computed({
  get: () => props.zoom,
  set: (value) => emit('update:zoom', value),
})

const schemeTileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
  attribution: '&copy; OpenStreetMap contributors',
  maxZoom: 19,
})

const satelliteTileLayer = L.tileLayer(
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  {
    attribution: 'Tiles &copy; Esri',
    maxZoom: 19,
  },
)

const hybridLabelsLayer = L.tileLayer(
  'https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png',
  {
    attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
    subdomains: 'abcd',
    maxZoom: 20,
    opacity: 0.85,
    pane: 'overlayPane',
    zIndex: 450,
  },
)

const layerRegistry: Record<MapType, L.Layer[]> = {
  scheme: [schemeTileLayer],
  satellite: [satelliteTileLayer],
  hybrid: [satelliteTileLayer, hybridLabelsLayer],
}

const syncActiveLayers = (map: LeafletMap, mapType: MapType) => {
  for (const layers of Object.values(layerRegistry)) {
    for (const layer of layers) {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer)
      }
    }
  }

  for (const layer of layerRegistry[mapType]) {
    if (!map.hasLayer(layer)) {
      layer.addTo(map)
    }
  }
}

/**
 * Разделяет точки по режимам Leaflet.
 * Selected и ungrouped всегда остаются отдельными, обычные точки уходят в cluster только на малом zoom.
 */
const getPointCollections = () => ({
  regularMarkers: [
    ...(props.objects?.selectedMarkers ?? []),
    ...(props.objects?.ungroupedSingles ?? []),
    ...(props.objects?.selectedUngroupedSingles ?? []),
    ...(props.zoom >= props.zoomSwitchValue && !props.objects?.zoomClusterMarkers?.length
      ? (props.objects?.markers ?? [])
      : []),
  ] as Array<InventObject | OffsetMarkerEntry>,
  clusterMarkers:
    props.zoom < props.zoomSwitchValue || props.objects?.zoomClusterMarkers?.length
      ? ([
          ...(props.objects?.markers ?? []),
          ...(props.objects?.zoomClusterMarkers ?? []),
        ] as InventObject[])
      : [],
  groupedMarkers:
    props.zoom >= props.zoomSwitchValue && !props.objects?.zoomClusterMarkers?.length
      ? ((props.objects?.groupMarkers ?? []) as GroupMarkerEntry[])
      : [],
})

const getMarkerOffset = (item: InventObject): [number, number] => {
  const offsetEntry = [
    ...(props.objects?.ungroupedSingles ?? []),
    ...(props.objects?.selectedUngroupedSingles ?? []),
  ].find((entry: { item: InventObject }) => entry.item.getId() === item.getId())

  return offsetEntry?.pixelOffset ?? [0, 0]
}

const getMarkerIcon = (
  offset: [number, number] = [0, 0],
  options: { isInfo?: boolean } = {},
): DivIcon => {
  const [offsetX, offsetY] = offset

  if (options.isInfo) {
    return L.divIcon({
      className: 'invent-leaflet-marker invent-leaflet-marker_info',
      html: '',
      iconSize: [1, 1],
      iconAnchor: [-offsetX, -offsetY],
    })
  }

  return L.divIcon({
    className: 'invent-leaflet-marker',
    html: '',
    iconSize: [48, 48],
    iconAnchor: [24 - offsetX, 24 - offsetY],
  })
}

const getStaticClusterIcon = (items: InventObject[]): DivIcon => {
  const clusterView = mountVueInLeaflet(VInventMapCluster, {
    items,
    disabled: props.selectedObjects.length > 0,
  })
  const html = clusterView.el.outerHTML

  clusterView.destroy()

  return L.divIcon({
    className: 'invent-leaflet-marker',
    html,
    iconSize: [56, 56],
    iconAnchor: [28, 28],
  })
}

const getMarkerContentHost = (markerElement: HTMLElement): HTMLElement | null => {
  if (markerElement.classList.contains('invent-leaflet-marker')) {
    return markerElement
  }

  return markerElement.querySelector('.invent-leaflet-marker')
}

const prepareLeafletTransformMarker = (markerElement: HTMLElement) => {
  const markerRoot = markerElement.querySelector<HTMLElement>('.v-transform-marker')

  if (markerRoot) {
    markerRoot.style.transform = 'none'
    return
  }

  markerElement.style.transform = 'none'
}

/**
 * Вставляет живой Vue DOM в divIcon после добавления marker на карту.
 * Это сохраняет поведение Vue-компонента и позволяет явно чистить его при rebuild.
 */
const attachMarkerContent = (
  marker: LeafletMarker,
  item: InventObject,
  offset: [number, number],
) => {
  markerObjectMap.set(marker, item)

  marker.on('add', () => {
    const markerElement = marker.getElement()
    if (!markerElement) return

    const contentEl = getMarkerContentHost(markerElement)
    if (!contentEl || contentEl.childElementCount > 0) return

    const markerView =
      props.action === 'move'
        ? mountVueInLeaflet(VInventMapMarker, {
            item,
            style: { ...props.getFeatureStyle(item) },
            small: props.zoom >= props.zoomSmallMarker && props.zoom <= props.zoomSwitchValue,
            dot: props.zoom < props.zoomSmallMarker,
          })
        : mountVueInLeaflet(VInventMapInfoMarker, {
            item,
            selected: props.selectedObjects,
            small: props.zoom >= props.zoomSmallMarker && props.zoom <= props.zoomSwitchValue,
            dot: props.zoom < props.zoomSmallMarker,
          })

    markerDestroyMap.get(marker)?.()
    markerDestroyMap.set(marker, markerView.destroy)

    markerView.el.style.position = 'relative'
    markerView.el.style.width = '100%'
    markerView.el.style.height = '100%'
    markerView.el.style.overflow = 'visible'
    markerView.el.style.pointerEvents = 'auto'

    contentEl.replaceChildren(markerView.el)
  })

  marker.on('click', (event) => {
    const originalEvent = event.originalEvent as MouseEvent | undefined
    const targetObject = markerObjectMap.get(marker)
    if (!targetObject) return
    props.objectClickHandler(targetObject, originalEvent ?? new MouseEvent('click'))
  })
}

/**
 * Приводит inventory-style к PathOptions Leaflet.
 * В controller stroke хранится массивом, а Leaflet ожидает плоские path options.
 */
const getLeafletFeatureStyle = (item: InventObject): PathOptions => {
  const style = props.getFeatureStyle(item)
  const stroke = Array.isArray(style.stroke) ? style.stroke[0] : style.stroke

  return {
    color: stroke?.color,
    weight: stroke?.width,
    dashArray: stroke?.dash?.length ? stroke.dash.join(' ') : undefined,
    fillColor: style.fill,
    fillOpacity: item.isPolygon() ? 0.35 : 0,
    opacity: 1,
    bubblingMouseEvents: true,
  }
}

const cloneFeatureCoordinates = (item: InventObject) =>
  JSON.parse(JSON.stringify(item.getMapCoords())) as DragLineCoordinates | DragPolygonCoordinates

const shiftLineCoordinates = (
  coordinates: DragLineCoordinates,
  deltaLng: number,
  deltaLat: number,
): DragLineCoordinates => coordinates.map(([lng, lat]) => [lng + deltaLng, lat + deltaLat])

const shiftPolygonCoordinates = (
  coordinates: DragPolygonCoordinates,
  deltaLng: number,
  deltaLat: number,
): DragPolygonCoordinates =>
  coordinates.map((line) => shiftLineCoordinates(line, deltaLng, deltaLat))

const setMapDraggingEnabled = (enabled: boolean) => {
  const map = leafletMap.value
  if (!map) return

  if (enabled) {
    map.dragging.enable()
  } else {
    map.dragging.disable()
  }
}

/**
 * Синхронизирует native drag Leaflet с режимом выбора области.
 * Без Alt drag отключен, с Alt временно возвращается перемещение карты.
 */
const syncAreaSelectDragging = () => {
  if (!props.selectCoordsType) {
    setMapDraggingEnabled(true)
    return
  }

  setMapDraggingEnabled(props.isAreaSelectMapDragEnabled)
}

/**
 * Обновляет Leaflet drag до внутренних mousedown-обработчиков карты.
 * Это нужно для Alt-drag: Leaflet решает, можно ли двигать карту, в начале mousedown.
 */
const areaSelectMouseDownCaptureHandler = (event: MouseEvent) => {
  if (!props.selectCoordsType) return

  setMapDraggingEnabled(event.altKey)
}

/**
 * Включает Leaflet drag сразу при нажатии Alt в режиме выбора области.
 * Это должно произойти до следующего mousedown, иначе Leaflet не начнет drag-жест.
 */
const areaSelectKeyDownHandler = (event: KeyboardEvent) => {
  if (!props.selectCoordsType || event.key !== 'Alt') return

  setMapDraggingEnabled(true)
}

/**
 * После отпускания Alt возвращает Leaflet в режим рисования области без перемещения карты.
 */
const areaSelectKeyUpHandler = (event: KeyboardEvent) => {
  if (!props.selectCoordsType || event.key !== 'Alt') return

  setMapDraggingEnabled(false)
}

const clearFeaturesLayer = () => {
  featuresLayer.value?.clearLayers()
}

const clearGroupedMarkersLayer = () => {
  groupedMarkersLayer.value?.clearLayers()
}

const clearClusterLayer = () => {
  clusterLayer.value?.clearLayers()
  clusterLayer.value?.off()
}

const clearPreviewLayer = () => {
  previewDestroyFns.forEach((destroy) => destroy())
  previewDestroyFns.clear()
  previewLayer.value?.clearLayers()
}

/**
 * Подключает единый обработчик клика к геометрическому слою Leaflet.
 */
const bindFeatureClick = (featureLayer: L.Path, item: InventObject) => {
  featureLayer.on('click', (event) => {
    const originalEvent = event.originalEvent as MouseEvent | undefined
    props.objectClickHandler(item, originalEvent ?? new MouseEvent('click'))
  })
}

/**
 * На этапе 4.3 рисуем только линии и полигоны.
 * Drag/edit handlers останутся на следующих шагах, поэтому здесь только style + click.
 */
const rebuildFeaturesLayer = () => {
  const layer = featuresLayer.value
  if (!layer) return

  clearFeaturesLayer()
  ;(props.objects?.features ?? []).forEach((item: InventObject) => {
    const featureStyle = getLeafletFeatureStyle(item)
    const lineCoordinates = item.isLine()
      ? lineToLeaflet(item.getMapCoords() as [number, number][])
      : null
    const featureLayer = item.isLine()
      ? L.polyline(lineCoordinates!, featureStyle)
      : L.polygon(polygonToLeaflet(item.getMapCoords() as [number, number][][]), featureStyle)

    bindFeatureClick(featureLayer, item)

    if (props.action === 'move' && props.canRelocateObject(item)) {
      featureLayer.on('mousedown', (event) => {
        if (item.isPoint()) return

        L.DomEvent.stopPropagation(event)
        activeFeatureDrag.value = {
          object: item,
          start: fromLeaflet(event.latlng) as [number, number],
          originalCoordinates: cloneFeatureCoordinates(item),
        }
        props.objectStartDragHandler(item)
        setMapDraggingEnabled(false)
      })
    }

    layer.addLayer(featureLayer)

    if (props.action === 'remove' && item.isLine() && lineCoordinates) {
      const removeHitLayer = L.polyline(lineCoordinates, {
        color: '#000000',
        weight: resolveInventoryLineRemoveHitWidth(featureStyle.weight),
        opacity: INVENTORY_LINE_REMOVE_HIT_OPACITY,
        interactive: true,
        bubblingMouseEvents: true,
        lineCap: 'round',
        lineJoin: 'round',
      })

      bindFeatureClick(removeHitLayer, item)
      layer.addLayer(removeHitLayer)
    }
  })
}

const clearMarkersLayer = () => {
  if (markersLayer.value) {
    markersLayer.value.eachLayer((layer) => {
      const marker = layer as LeafletMarker
      markerDestroyMap.get(marker)?.()
      markerDestroyMap.delete(marker)
    })

    markersLayer.value.clearLayers()
  }
}

const createObjectMarker = (item: InventObject, options: ObjectMarkerOptions = {}) => {
  const offset = options.offset ?? [0, 0]
  const coordinates = options.coordinates ?? (item.getMapCoords() as [number, number])
  const canRelocate = props.canRelocateObject(item)
  const allowDrag = options.allowDrag ?? true
  const marker = L.marker(toLeaflet(coordinates), {
    icon: getMarkerIcon(offset, { isInfo: props.action !== 'move' }),
    draggable: allowDrag && props.action === 'move' && canRelocate,
    zIndexOffset: props.selectedObjects.includes(item.getId()) ? 1000 : 0,
  })

  attachMarkerContent(marker, item, offset)

  if (allowDrag && props.action === 'move' && canRelocate) {
    marker.on('dragstart', () => {
      props.objectStartDragHandler(item)
    })

    marker.on('drag', () => {
      props.objectMoveDragHandler(item, fromLeaflet(marker.getLatLng()))
    })

    marker.on('dragend', () => {
      props.objectEndDragHandler(item, fromLeaflet(marker.getLatLng()))
    })
  }

  /* Метка без прав на перемещение: drag заблокирован, по mousedown предупреждаем */
  if (allowDrag && props.action === 'move' && !canRelocate) {
    marker.on('mousedown', () => {
      props.objectMoveBlockedHandler(item)
    })
  }

  return marker
}

const rebuildMarkersLayer = () => {
  const layer = markersLayer.value
  if (!layer) return

  clearMarkersLayer()

  getPointCollections().regularMarkers.forEach((entry) => {
    const item = isOffsetMarkerEntry(entry) ? entry.item : entry
    const offset = isOffsetMarkerEntry(entry) ? entry.pixelOffset : getMarkerOffset(item)

    layer.addLayer(createObjectMarker(item, { offset }))
  })
}

const rebuildGroupedMarkersLayer = () => {
  const layer = groupedMarkersLayer.value
  if (!layer) return

  clearGroupedMarkersLayer()

  getPointCollections().groupedMarkers.forEach((group) => {
    const marker = L.marker(toLeaflet(group.coordinates), {
      icon: getStaticClusterIcon(group.items),
      zIndexOffset: 500,
    })

    marker.on('click', () => {
      props.clusterSelectHandler(group.items.map((item) => item.getId()))
    })

    layer.addLayer(marker)
  })
}

const rebuildClusterLayer = () => {
  const layer = clusterLayer.value
  if (!layer || !isMarkerClusterReady.value) return

  clearClusterLayer()

  getPointCollections().clusterMarkers.forEach((item) => {
    layer.addLayer(
      createObjectMarker(item, {
        coordinates: item.getLabelCoords() as [number, number],
        allowDrag: item.isPoint(),
      }),
    )
  })

  layer.on('clusterclick', (event) => {
    const ids = event.layer
      .getAllChildMarkers()
      .map((marker) => markerObjectMap.get(marker as LeafletMarker)?.getId())
      .filter((id): id is number => Number.isFinite(id))

    props.clusterSelectHandler(ids)
  })
}

const ensureMarkerCluster = async () => {
  if (isMarkerClusterReady.value)
    return // leaflet.markercluster expects a global L during module evaluation.
  ;(globalThis as typeof globalThis & { L?: typeof L }).L = L
  await import('leaflet.markercluster')
  isMarkerClusterReady.value = true
}

/**
 * Drawing preview живёт отдельно от object layers:
 * здесь только временная геометрия create-mode и optional marker для вставки новой точки.
 */
const rebuildPreviewLayer = () => {
  const layer = previewLayer.value
  if (!layer) return
  if (isEditMarkerDragging.value) return

  clearPreviewLayer()

  if (props.editSelectObject) {
    ;(props.editSelectObject.getTransformCoords() as [number, number][]).forEach(
      (coords, index) => {
        const markerView = mountVueInLeaflet(VTransformMarker)
        prepareLeafletTransformMarker(markerView.el)
        const marker = L.marker(toLeaflet(coords), {
          draggable: true,
          icon: L.divIcon({
            className: 'invent-leaflet-marker',
            html: '',
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          }),
          zIndexOffset: 1300,
        })

        previewDestroyFns.add(markerView.destroy)

        marker.on('add', () => {
          const markerElement = marker.getElement()
          const contentEl = markerElement ? getMarkerContentHost(markerElement) : null

          if (!contentEl || contentEl.childElementCount > 0) return

          contentEl.replaceChildren(markerView.el)
        })

        marker.on('dragstart', () => {
          isEditMarkerDragging.value = true
          props.dragStartEditHandler()
        })

        marker.on('drag', () => {
          props.dragMoveEditHandler(index, fromLeaflet(marker.getLatLng()))
          rebuildFeaturesLayer()
        })

        marker.on('dragend', () => {
          props.dragEndEditHandler()
          isEditMarkerDragging.value = false
          rebuildFeaturesLayer()
          rebuildPreviewLayer()
        })

        marker.on('click', (event) => {
          const originalEvent = event.originalEvent as MouseEvent | undefined
          props.clickEditHandler(index, originalEvent ?? new MouseEvent('click'))
        })

        layer.addLayer(marker)
      },
    )
  }

  if (props.pointerCount && props.editFeature?.geometry) {
    const { geometry, style } = props.editFeature
    const stroke = Array.isArray(style?.stroke) ? style.stroke[0] : style?.stroke
    const previewStyle: PathOptions = {
      color: stroke?.color,
      weight: stroke?.width,
      dashArray: stroke?.dash?.length ? stroke.dash.join(' ') : undefined,
      fillColor: style?.fill,
      fillOpacity: geometry.type === 'Polygon' ? 0.35 : 0,
      opacity: 1,
    }

    const previewGeometry =
      geometry.type === 'LineString'
        ? L.polyline(lineToLeaflet(geometry.coordinates as [number, number][]), previewStyle)
        : L.polygon(polygonToLeaflet(geometry.coordinates as DrawPolygonCoordinates), previewStyle)

    layer.addLayer(previewGeometry)
  }

  if (props.editNewPoint?.geometry?.coordinates && props.isEditNewPoint) {
    const markerView = mountVueInLeaflet(VTransformMarker, { isOptional: true })
    prepareLeafletTransformMarker(markerView.el)
    const marker = L.marker(
      toLeaflet(props.editNewPoint.geometry.coordinates as [number, number]),
      {
        icon: L.divIcon({
          className: 'invent-leaflet-marker',
          html: '',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
        zIndexOffset: 1200,
      },
    )

    previewDestroyFns.add(markerView.destroy)

    marker.on('add', () => {
      const markerElement = marker.getElement()
      const contentEl = markerElement ? getMarkerContentHost(markerElement) : null

      if (!contentEl || contentEl.childElementCount > 0) return

      contentEl.replaceChildren(markerView.el)
    })

    marker.on('click', (event) => {
      L.DomEvent.stopPropagation(event)
      props.clickAddPointHandler()
    })

    layer.addLayer(marker)
  }
}

const flushPendingEntityActions = () => {
  const currentEntity = adapterState.value?.entity
  if (!currentEntity) return

  if (pendingLocation.value) {
    currentEntity.setLocation(pendingLocation.value)
    pendingLocation.value = null
  }

  if (pendingBehaviors.value) {
    currentEntity.setBehaviors(pendingBehaviors.value)
    pendingBehaviors.value = null
  }
}

const entity: MapAdapterEntity = {
  get center() {
    return adapterState.value?.entity.center
  },
  get zoom() {
    return adapterState.value?.entity.zoom
  },
  get bounds() {
    return adapterState.value?.entity.bounds
  },
  get behaviors() {
    return adapterState.value?.entity.behaviors ?? ['drag']
  },
  setLocation(location) {
    if (!adapterState.value) {
      pendingLocation.value = location
      return
    }

    adapterState.value.entity.setLocation(location)
  },
  getBounds() {
    return adapterState.value?.entity.getBounds()
  },
  setBehaviors(next) {
    if (!adapterState.value) {
      pendingBehaviors.value = next
      return
    }

    adapterState.value.entity.setBehaviors(next)
  },
}

const toMapMousePayload = (event: LeafletMouseEvent) => {
  const originalEvent = event.originalEvent as MouseEvent | undefined

  return {
    coordinates: fromLeaflet(event.latlng),
    screenCoordinates: [originalEvent?.clientX ?? 0, originalEvent?.clientY ?? 0] as [
      number,
      number,
    ],
    details: {
      shiftKey: !!originalEvent?.shiftKey,
      altKey: !!originalEvent?.altKey,
      ctrlKey: !!originalEvent?.ctrlKey,
      metaKey: !!originalEvent?.metaKey,
    },
  }
}

const handleFeatureDragMove = (event: LeafletMouseEvent) => {
  if (!activeFeatureDrag.value) return

  const { object, start, originalCoordinates } = activeFeatureDrag.value
  const [lng, lat] = fromLeaflet(event.latlng)
  const deltaLng = lng - start[0]
  const deltaLat = lat - start[1]

  const nextCoordinates = object.isPolygon()
    ? shiftPolygonCoordinates(originalCoordinates as DragPolygonCoordinates, deltaLng, deltaLat)
    : shiftLineCoordinates(originalCoordinates as DragLineCoordinates, deltaLng, deltaLat)

  object.getFeature().geometry = {
    ...object.getFeature().geometry,
    coordinates: nextCoordinates,
  }

  props.objectMoveDragHandler(object as InventObject, nextCoordinates)
  rebuildFeaturesLayer()
  rebuildPreviewLayer()
}

const handleFeatureDragEnd = () => {
  if (!activeFeatureDrag.value) return

  const { object } = activeFeatureDrag.value
  const coordinates = object.getMapCoords()

  activeFeatureDrag.value = null
  setMapDraggingEnabled(true)
  props.objectEndDragHandler(object as InventObject, coordinates)
  rebuildFeaturesLayer()
  rebuildPreviewLayer()
}

const emitMapUpdate = () => {
  if (!adapterState.value) return

  adapterState.value.syncFromMap()

  const location = {
    center: adapterState.value.entity.center ?? centerModel.value,
    zoom: adapterState.value.entity.zoom ?? zoomModel.value,
  }

  centerModel.value = location.center
  zoomModel.value = location.zoom
  props.updateHandler({ location })
}

watch(
  () => [props.center[0], props.center[1], props.zoom] as const,
  ([lng, lat, zoom]) => {
    if (!leafletMap.value || isApplyingExternalLocation.value) return

    const currentCenter = leafletMap.value.getCenter()
    const sameCenter = currentCenter.lng === lng && currentCenter.lat === lat
    const sameZoom = leafletMap.value.getZoom() === zoom

    if (sameCenter && sameZoom) return

    isApplyingExternalLocation.value = true
    adapterState.value?.entity.setLocation({ center: [lng, lat], zoom })
    isApplyingExternalLocation.value = false
  },
)

watch(
  () => props.mapType,
  (mapType) => {
    const map = leafletMap.value as LeafletMap | null
    if (map) {
      syncActiveLayers(map, mapType)
    }
  },
)

watch(
  () => [
    props.objects,
    props.action,
    props.zoom,
    props.zoomSmallMarker,
    props.zoomSwitchValue,
    props.selectedObjects,
  ],
  () => {
    rebuildMarkersLayer()
    rebuildGroupedMarkersLayer()
    rebuildClusterLayer()
  },
  { deep: true },
)

watch(
  () => [props.objects, props.selectedObjects, props.action],
  () => {
    rebuildFeaturesLayer()
  },
  { deep: true },
)

watch(
  () => [
    props.editFeature,
    props.pointerCount,
    props.editNewPoint,
    props.isEditNewPoint,
    props.editSelectObject,
  ],
  () => {
    rebuildPreviewLayer()
  },
  { deep: true },
)

watch(
  () => [props.selectCoordsType, props.isAreaSelectMapDragEnabled],
  () => {
    syncAreaSelectDragging()
  },
)

onMounted(() => {
  if (!mapEl.value) return
  document.addEventListener('keydown', areaSelectKeyDownHandler)
  document.addEventListener('keyup', areaSelectKeyUpHandler)
  mapEl.value.addEventListener('mousedown', areaSelectMouseDownCaptureHandler, {
    capture: true,
  })

  const mountLeaflet = async () => {
    await ensureMarkerCluster()

    const map = L.map(mapEl.value as HTMLElement, {
      zoomControl: false,
      attributionControl: false,
      boxZoom: false,
    }).setView(toLeaflet(props.center as [number, number]), props.zoom)

    syncActiveLayers(map, props.mapType)

    leafletMap.value = map
    markersLayer.value = L.layerGroup().addTo(map)
    featuresLayer.value = L.layerGroup().addTo(map)
    groupedMarkersLayer.value = L.layerGroup().addTo(map)
    previewLayer.value = L.layerGroup().addTo(map)
    clusterLayer.value = L.markerClusterGroup({
      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) =>
        getStaticClusterIcon(
          cluster
            .getAllChildMarkers()
            .map((marker) => markerObjectMap.get(marker as LeafletMarker))
            .filter((item): item is InventObject => !!item),
        ),
    }).addTo(map)
    adapterState.value = createLeafletAdapter(map)
    flushPendingEntityActions()
    syncAreaSelectDragging()
    rebuildMarkersLayer()
    rebuildFeaturesLayer()
    rebuildGroupedMarkersLayer()
    rebuildClusterLayer()
    rebuildPreviewLayer()

    map.on('moveend zoomend', () => {
      if (isApplyingExternalLocation.value) return
      emitMapUpdate()
    })

    map.on('mousemove', (event) => {
      handleFeatureDragMove(event)
      props.mouseMoveHandler(null, toMapMousePayload(event))
    })

    map.on('click', (event) => {
      props.clickHandler(null, toMapMousePayload(event))
    })

    map.on('mouseup', (event) => {
      handleFeatureDragEnd()
      props.mouseUpHandler(null, toMapMousePayload(event))
    })

    map.on('mousedown', (event) => {
      props.mouseDownHandler(null, toMapMousePayload(event))
    })

    emitMapUpdate()
  }

  void mountLeaflet()
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', areaSelectKeyDownHandler)
  document.removeEventListener('keyup', areaSelectKeyUpHandler)
  mapEl.value?.removeEventListener('mousedown', areaSelectMouseDownCaptureHandler, {
    capture: true,
  })
  clearMarkersLayer()
  markersLayer.value?.remove()
  markersLayer.value = null
  clearFeaturesLayer()
  featuresLayer.value?.remove()
  featuresLayer.value = null
  clearGroupedMarkersLayer()
  groupedMarkersLayer.value?.remove()
  groupedMarkersLayer.value = null
  clearClusterLayer()
  clusterLayer.value?.remove()
  clusterLayer.value = null
  clearPreviewLayer()
  previewLayer.value?.remove()
  previewLayer.value = null
  leafletMap.value?.remove()
  leafletMap.value = null
  adapterState.value = null
})

defineExpose<MapComponentExpose>({
  entity,
})
</script>

<template>
  <div ref="mapEl" class="w-invent-map__wrapper"></div>
</template>
